// Density and carrying capacity, P1 (findings #96). The unit type is a fixed
// list kept in `ProductionUnit.metadata.type`; the farm's density limits live in
// `Farm.metadata.densityLimits` (no migration). GLOBALG.A.P. AQ-GFS 20.02.13
// (Major Must: the farm sets maximum densities by size, stage and system, never
// exceeds them, calculates and records them); ASC 4.1.1.12.

export type UnitType = 'floatingCage' | 'circularTank' | 'raceway' | 'pond' | 'incubator' | 'fibreglassTank'
export const UNIT_TYPES: UnitType[] = ['floatingCage', 'circularTank', 'raceway', 'pond', 'incubator', 'fibreglassTank']

export const isUnitType = (value: unknown): value is UnitType =>
  typeof value === 'string' && (UNIT_TYPES as string[]).includes(value)

/**
 * One weight band: lots whose average weight is up to `maxWeightG` (the last
 * band has `maxWeightG: null` = "more than the previous band"). `limits` holds
 * the maximum kg/m³ per unit type; a missing type means "no limit set".
 */
export type DensityLimitBand = {
  maxWeightG: number | null
  limits: Partial<Record<UnitType, number>>
}

type WithMetadata = { metadata?: Record<string, unknown> | null } | null | undefined

const positive = (value: unknown): number | null => {
  const parsed = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : Number.NaN
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

/** The unit's useful water volume in m³, or null when it is not set. */
export const unitVolumeM3 = (capacity: unknown, capacityUnit: unknown): number | null => {
  const value = positive(capacity)
  if (value === null) return null
  if (capacityUnit === 'L') return value / 1000
  if (capacityUnit === 'ha') return value * 10000
  return value
}

/** kg/m³, or null when the unit has no volume (shown as "—", never 0.0). */
export const stockingDensityKgM3 = (biomassKg: number, volumeM3: number | null): number | null => {
  if (volumeM3 === null || !(volumeM3 > 0) || !Number.isFinite(biomassKg) || biomassKg < 0) return null
  return biomassKg / volumeM3
}

/** L/min per kg of fish, or null without flow or biomass. */
export const flowPerKg = (flowLMin: number | null | undefined, biomassKg: number): number | null => {
  if (typeof flowLMin !== 'number' || !Number.isFinite(flowLMin) || flowLMin <= 0) return null
  if (!(biomassKg > 0)) return null
  return flowLMin / biomassKg
}

/** Bands sorted by weight, the open-ended band last. Invalid entries are dropped. */
export const readDensityLimits = (farm: WithMetadata): DensityLimitBand[] => {
  const raw = farm?.metadata?.densityLimits
  if (!Array.isArray(raw)) return []
  const bands: DensityLimitBand[] = []
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue
    const record = entry as Record<string, unknown>
    const maxWeightG = record.maxWeightG === null || record.maxWeightG === undefined ? null : positive(record.maxWeightG)
    if (record.maxWeightG !== null && record.maxWeightG !== undefined && maxWeightG === null) continue
    const limits: Partial<Record<UnitType, number>> = {}
    const rawLimits = record.limits && typeof record.limits === 'object' ? record.limits as Record<string, unknown> : {}
    for (const type of UNIT_TYPES) {
      const limit = positive(rawLimits[type])
      if (limit !== null) limits[type] = limit
    }
    bands.push({ maxWeightG, limits })
  }
  return sortBands(bands)
}

export const sortBands = (bands: DensityLimitBand[]): DensityLimitBand[] =>
  [...bands].sort((a, b) => (a.maxWeightG ?? Number.POSITIVE_INFINITY) - (b.maxWeightG ?? Number.POSITIVE_INFINITY))

/** The farm's limit for a lot of this average weight in this unit type, or null. */
export const densityLimitFor = (
  bands: DensityLimitBand[],
  unitType: unknown,
  averageWeightG: number | null | undefined
): number | null => {
  if (!isUnitType(unitType)) return null
  if (typeof averageWeightG !== 'number' || !Number.isFinite(averageWeightG) || averageWeightG <= 0) return null
  const band = sortBands(bands).find((candidate) => candidate.maxWeightG === null || averageWeightG <= candidate.maxWeightG)
  return band?.limits[unitType] ?? null
}

export const exceedsDensityLimit = (densityKgM3: number | null, limitKgM3: number | null): boolean =>
  densityKgM3 !== null && limitKgM3 !== null && densityKgM3 > limitKgM3
